// Pure helpers that drive the sidebar room list.
//
// Kept deliberately free of React and Matrix bindings so each piece can
// be tested in isolation and composed by the sidebar without an
// intervening hook. The live list arrives via `subscribeRooms`; the
// sidebar categorises, sorts, and filters it through these functions.

import type { RoomMember, RoomSummary, TimelineEvent } from "~/lib/matrix";

/** Looks up the synced member list for a room. Sidebar callers pass a
 *  cache reader (`getMembers`); omitting it keeps name/topic matching. */
export type RoomMembersLookup = (roomId: string) => readonly RoomMember[];

export interface CategorisedRooms {
  channels: RoomSummary[];
  dms: RoomSummary[];
}

/**
 * Split a flat room list into channels and direct messages using the
 * `isDirect` projection of `m.direct` account data. A room is a DM iff
 * `isDirect` is true; everything else is a channel. This is the single
 * rule the brief pins, so all consumers share one answer.
 *
 * Ordering inside each bucket is preserved; call `sortByActivity` after
 * to get the chronological order the sidebar shows.
 */
export function categorize(rooms: RoomSummary[]): CategorisedRooms {
  const channels: RoomSummary[] = [];
  const dms: RoomSummary[] = [];
  for (const room of rooms) {
    if (room.isDirect) dms.push(room);
    else channels.push(room);
  }
  return { channels, dms };
}

/** Single-room variant of the `isDirect` rule `categorize` applies. */
export function isDmRoom(rooms: RoomSummary[], roomId: string | null): boolean {
  if (!roomId) return false;
  return rooms.find((room) => room.id === roomId)?.isDirect === true;
}

/**
 * Sort by `lastActivityTs` descending — most recent first. Returns a new
 * array so the caller's input is not mutated. A tie on `lastActivityTs`
 * falls back to `name` for stable, deterministic ordering across
 * re-renders; otherwise React's reconciler would see phantom position
 * swaps every time two rooms share a timestamp.
 */
export function sortByActivity(rooms: RoomSummary[]): RoomSummary[] {
  return [...rooms].sort((a, b) => {
    if (b.lastActivityTs !== a.lastActivityTs) return b.lastActivityTs - a.lastActivityTs;
    return a.name.localeCompare(b.name);
  });
}

/**
 * Case-insensitive substring match over name, topic, and people
 * (member display names / user ids, plus a DM's `directUserIds`).
 * Empty / whitespace-only queries are a pass-through. The optional
 * `getMembers` reader is consulted only when the cheaper dimensions
 * miss, so a name/topic hit never touches the member cache.
 */
export function filterRooms(
  rooms: RoomSummary[],
  query: string,
  getMembers?: RoomMembersLookup,
): RoomSummary[] {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return rooms;
  return rooms.filter((room) => roomMatches(room, needle, getMembers));
}

function roomMatches(room: RoomSummary, needle: string, getMembers?: RoomMembersLookup): boolean {
  if (room.name.toLowerCase().includes(needle)) return true;
  if (room.topic !== null && room.topic.toLowerCase().includes(needle)) return true;
  if (room.directUserIds.some((id) => id.toLowerCase().includes(needle))) return true;
  if (!getMembers) return false;
  return getMembers(room.id).some(
    (member) =>
      member.userId.toLowerCase().includes(needle) ||
      member.displayName.toLowerCase().includes(needle),
  );
}

/**
 * User id → `mxc://` avatar for read-receipt pills. Senders in the buffer
 * seed it (they resolve before the member list loads); members overlay.
 */
export function buildReceiptAvatarMap(
  events: readonly Pick<TimelineEvent, "sender">[],
  members: readonly Pick<RoomMember, "userId" | "avatarUrl">[],
): Map<string, string> {
  const map = new Map<string, string>();
  for (const { sender } of events) {
    if (sender.avatarUrl?.startsWith("mxc://")) map.set(sender.id, sender.avatarUrl);
  }
  for (const member of members) {
    if (member.avatarUrl?.startsWith("mxc://")) map.set(member.userId, member.avatarUrl);
  }
  return map;
}
